import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Wine, CreditCard, Gift, Share2, Download } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import { QRCodeCanvas } from 'qrcode.react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SVCSo382aFGgdDTM8fb8m063DuKfpCwaW8SSpAZ3KBkKMkGeEVhUkMUeA22P7hQwe04Bxs5eqRx0NhhAjdS87Qm001YK7K9gw');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#E6D7C3',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#8A7355',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

function PaymentForm({ drink, venue, onSuccess, recipientInfo, message, customInstructions, customDrinkName }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      // Create payment intent
      const { data } = await axios.post(`${API}/drinks/purchase`, {
        venue_id: venue.id,
        drink_id: drink.id,
        recipient_info: recipientInfo,
        personal_message: message,
        custom_instructions: customInstructions,
        custom_drink_name: customDrinkName
      });

      // Confirm payment
      const result = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else {
        // Payment successful, confirm on backend
        const confirmRes = await axios.post(`${API}/drinks/confirm-payment/${data.transaction_id}`);
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
      <div style={{
        background: 'rgba(230, 215, 195, 0.05)',
        border: '1px solid #A57C33',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {error && (
        <div style={{
          padding: '12px',
          background: 'rgba(220, 38, 38, 0.1)',
          border: '1px solid rgba(220, 38, 38, 0.3)',
          borderRadius: '8px',
          color: '#fca5a5',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        style={{
          width: '100%',
          padding: '16px',
          background: processing ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
          color: '#2C1B13',
          border: 'none',
          borderRadius: '12px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: processing ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.3s ease'
        }}
      >
        <CreditCard size={20} />
        {processing ? 'Processing...' : `Pay $${drink.price.toFixed(2)}`}
      </button>
    </form>
  );
}

const SendDrinkPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: venues, 2: drinks, 3: details, 4: payment, 5: success
  const [venues, setVenues] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState('');
  const [message, setMessage] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [customDrinkName, setCustomDrinkName] = useState('');
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/drinks/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error('Error fetching venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrinks = async (venueId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/drinks/venues/${venueId}/menu`);
      setDrinks(response.data);
      setStep(2);
    } catch (error) {
      console.error('Error fetching drinks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVenueSelect = (venue) => {
    setSelectedVenue(venue);
    fetchDrinks(venue.id);
  };

  const handleDrinkSelect = (drink) => {
    setSelectedDrink(drink);
    setStep(3);
  };

  const handlePaymentSuccess = (data) => {
    setQrData(data);
    setStep(5);
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `drink-qr-${qrData.unique_code}.png`;
    link.href = url;
    link.click();
  };

  const shareQRCode = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${qrData.drink_name} at ${qrData.venue_name}`,
          text: `I sent you a drink! ${message ? message : 'Enjoy!'}`,
          url: qrData.qr_url
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(qrData.qr_url);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div style={{ paddingBottom: '80px', minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)' }}>
      <div className="page-header">
        <button
          onClick={() => step === 1 ? navigate(-1) : step === 2 ? setStep(1) : step === 3 ? setStep(2) : step === 4 ? setStep(3) : setStep(1)}
          style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer', padding: '8px' }}
        >
          <ArrowLeft size={24} />
        </button>
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          {step === 5 ? 'DRINK SENT!' : 'SEND A DRINK'}
        </h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Progress indicator */}
        {step < 5 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', justifyContent: 'center' }}>
            {[1, 2, 3, 4].map(s => (
              <div
                key={s}
                style={{
                  width: '40px',
                  height: '4px',
                  background: s <= step ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.2)',
                  borderRadius: '2px',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        )}

        {/* Step 1: Select Venue */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Select Venue
            </h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading venues...</p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {venues.map(venue => (
                  <div
                    key={venue.id}
                    onClick={() => handleVenueSelect(venue)}
                    style={{
                      background: 'rgba(230, 215, 195, 0.05)',
                      border: '1px solid #A57C33',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      gap: '16px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.05)'}
                  >
                    {venue.photo && (
                      <img
                        src={venue.photo}
                        alt={venue.name}
                        style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '18px', color: '#E6D7C3', marginBottom: '4px', fontWeight: 'bold' }}>
                        {venue.name}
                      </h3>
                      <p style={{ fontSize: '14px', color: '#A57C33', marginBottom: '4px' }}>
                        <MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} />
                        {venue.address}, {venue.city}
                      </p>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: 'rgba(215, 181, 109, 0.2)',
                        color: '#D7B56D',
                        borderRadius: '4px',
                        fontSize: '12px',
                        textTransform: 'capitalize'
                      }}>
                        {venue.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Drink */}
        {step === 2 && selectedVenue && (
          <div>
            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '4px' }}>Selected Venue</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold' }}>{selectedVenue.name}</h3>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Select Drink
            </h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading menu...</p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {drinks.map(drink => (
                  <div
                    key={drink.id}
                    onClick={() => handleDrinkSelect(drink)}
                    style={{
                      background: 'rgba(230, 215, 195, 0.05)',
                      border: '1px solid #A57C33',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      gap: '16px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.05)'}
                  >
                    {drink.photo && (
                      <img
                        src={drink.photo}
                        alt={drink.name}
                        style={{ width: '70px', height: '70px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <h3 style={{ fontSize: '18px', color: '#E6D7C3', marginBottom: '4px', fontWeight: 'bold' }}>
                          {drink.name}
                        </h3>
                        <span style={{
                          fontSize: '18px',
                          color: '#D7B56D',
                          fontWeight: 'bold'
                        }}>
                          ${drink.price.toFixed(2)}
                        </span>
                      </div>
                      <p style={{ fontSize: '14px', color: '#8A7355', marginBottom: '6px' }}>
                        {drink.description}
                      </p>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: 'rgba(215, 181, 109, 0.2)',
                        color: '#D7B56D',
                        borderRadius: '4px',
                        fontSize: '12px',
                        textTransform: 'capitalize'
                      }}>
                        {drink.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Add Details */}
        {step === 3 && selectedDrink && (
          <div>
            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {selectedDrink.photo && (
                  <img
                    src={selectedDrink.photo}
                    alt={selectedDrink.name}
                    style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <p style={{ color: '#A57C33', fontSize: '13px' }}>{selectedVenue.name}</p>
                  <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold' }}>{selectedDrink.name}</h3>
                  <p style={{ color: '#D7B56D', fontSize: '16px', fontWeight: 'bold' }}>${selectedDrink.price.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Add Details
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>
                Recipient Info (Optional)
              </label>
              <input
                type="text"
                placeholder="Name, email, or phone"
                value={recipientInfo}
                onChange={(e) => setRecipientInfo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(230, 215, 195, 0.05)',
                  border: '1px solid #A57C33',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>
                Personal Message (Optional)
              </label>
              <textarea
                placeholder="Add a personal message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(230, 215, 195, 0.05)',
                  border: '1px solid #A57C33',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '16px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Customization Section */}
            {(selectedDrink.customizable || selectedDrink.is_custom) && (
              <>
                <div style={{
                  background: 'rgba(215, 181, 109, 0.1)',
                  border: '1px solid #D7B56D',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ color: '#D7B56D', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                    🎨 Customize Your Drink
                  </h3>
                  
                  {selectedDrink.is_custom && (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>
                        Drink Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Espresso Martini"
                        value={customDrinkName}
                        onChange={(e) => setCustomDrinkName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'rgba(230, 215, 195, 0.05)',
                          border: '1px solid #A57C33',
                          borderRadius: '8px',
                          color: '#E6D7C3',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  )}
                  
                  <div>
                    <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>
                      Special Instructions
                    </label>
                    <textarea
                      placeholder="e.g., with Casa Amigos Blanco, extra espresso, no sugar..."
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.05)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '16px',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                    <p style={{ color: '#8A7355', fontSize: '12px', marginTop: '6px' }}>
                      Specify premium spirits, modifications, or create your own unique drink
                    </p>
                  </div>
                </div>
              </>
            )}

            <button
              onClick={() => setStep(4)}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                color: '#2C1B13',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Gift size={20} />
              Continue to Payment
            </button>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && selectedDrink && (
          <div>
            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#E6D7C3', fontSize: '16px', marginBottom: '12px' }}>Order Summary</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#A57C33' }}>{selectedDrink.name}</span>
                <span style={{ color: '#E6D7C3' }}>${selectedDrink.price.toFixed(2)}</span>
              </div>
              <div style={{ borderTop: '1px solid #A57C33', marginTop: '12px', paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold' }}>Total</span>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '18px' }}>${selectedDrink.price.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Payment Details
            </h2>

            <Elements stripe={stripePromise}>
              <PaymentForm
                drink={selectedDrink}
                venue={selectedVenue}
                onSuccess={handlePaymentSuccess}
                recipientInfo={recipientInfo}
                message={message}
                customInstructions={customInstructions}
                customDrinkName={customDrinkName || selectedDrink.name}
              />
            </Elements>
          </div>
        )}

        {/* Step 5: Success with QR Code */}
        {step === 5 && qrData && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Gift size={40} color="#2C1B13" />
            </div>

            <h2 style={{
              fontSize: '28px',
              color: '#E6D7C3',
              marginBottom: '12px',
              fontFamily: 'Playfair Display, serif'
            }}>
              Drink Sent!
            </h2>

            <p style={{ color: '#A57C33', marginBottom: '32px', fontSize: '16px' }}>
              Share this QR code with the recipient
            </p>

            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '16px',
              display: 'inline-block',
              marginBottom: '24px'
            }}>
              <QRCodeCanvas
                id="qr-code-canvas"
                value={qrData.qr_url}
                size={250}
                level="H"
              />
            </div>

            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '8px' }}>Drink Details</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                {qrData.drink_name}
              </h3>
              <p style={{ color: '#D7B56D' }}>{qrData.venue_name}</p>
              <p style={{ color: '#8A7355', fontSize: '14px', marginTop: '8px' }}>
                Code: {qrData.unique_code}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <button
                onClick={shareQRCode}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                  color: '#2C1B13',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Share2 size={20} />
                Share
              </button>
              <button
                onClick={downloadQRCode}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'rgba(230, 215, 195, 0.1)',
                  color: '#E6D7C3',
                  border: '1px solid #A57C33',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Download size={20} />
                Download
              </button>
            </div>

            <button
              onClick={() => navigate('/profile/drink-history')}
              style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(230, 215, 195, 0.05)',
                color: '#D7B56D',
                border: '1px solid #A57C33',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginBottom: '12px'
              }}
            >
              View Drink History
            </button>

            <button
              onClick={() => {
                setStep(1);
                setSelectedVenue(null);
                setSelectedDrink(null);
                setRecipientInfo('');
                setMessage('');
                setQrData(null);
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: 'none',
                color: '#A57C33',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Send Another Drink
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendDrinkPage;
